<template>
  <div class="cont">
    <div style="width: 100%; display: flex; align-items: center;">
      <h2 class="page-title">{{ playlistName }}</h2>
    </div>

    <transition name="fade" mode="out-in">
      <div v-if="isLoading" class="state-message">
        <div class="loader"></div>
      </div>
      <div v-else-if="!tracks.length" class="state-message">
        <v-icon size="48" color="#555">mdi-music-note-off-outline</v-icon>
        <p>No songs in this playlist yet.</p>
      </div>
      <div v-else class="list">
        <div class="listItem" v-for="track in tracks" :key="track.id" @click="play(track)">
          <div class="thumb_cont">
            <div class="img-cont">
              <img class="thumbnail" :src="track.thumbnails?.[0]?.url" alt="" />
            </div>
          </div>
          <div class="info">
            <div style="text-align: left; width: 70%; overflow: hidden;">
              <p class="track-title">{{ track.title }}</p>
              <p v-for="(artist, index) in track.artist" :key="index" class="artist-name">{{ artist.name }}</p>
            </div>
            <div style="display: flex; align-items: center; gap: 10px;">
              <LikeButton></LikeButton>
              <v-icon color="#fff" style="cursor: pointer;" @click.stop="removeSong(track.id)">mdi-close</v-icon>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { useToast } from 'vue-toastification';
import { useQueueStore } from '../stores/queue';
import { usePlaylistsStore } from '../stores/playlists';
import { EventBus } from '../eventBus';
import LikeButton from './likeButton.vue';

export default {
  name: 'UserPlaylistDetailComponent',
  components: { LikeButton },
  setup() {
    const queueStore = useQueueStore();
    const playlistsStore = usePlaylistsStore();
    return { queueStore, playlistsStore };
  },
  data() {
    return {
      toast: useToast(),
      prodUrl: process.env.VUE_APP_PROD_URL,
      isLoading: true,
      playlistName: '',
      tracks: [],
    };
  },
  mounted() {
    this.load();
  },
  methods: {
    goBack() {
      if (window.history.state && window.history.state.back) {
        this.$router.back();
      } else {
        this.$router.push('/playlists');
      }
    },
    async load() {
      this.isLoading = true;
      try {
        const playlist = await this.playlistsStore.getPlaylist(this.$route.params.id);
        if (!playlist) {
          this.toast.error('Playlist not found');
          this.$router.push('/playlists');
          return;
        }
        this.playlistName = playlist.name;

        const videoIds = (playlist.songs || []).map((s) => s.video_id);
        const infos = await Promise.all(
          videoIds.map((id) =>
            fetch(`${this.prodUrl}/music/getInfo?id=${id}`).then((res) => res.json()).catch(() => null)
          )
        );
        this.tracks = infos.filter(Boolean).map((info) => ({ ...info, id: info.id }));
      } catch (err) {
        console.error('Error loading playlist:', err);
        this.toast.error('Could not load playlist.');
      } finally {
        this.isLoading = false;
      }
    },
    play(track) {
      this.queueStore.emptyQueue();
      this.queueStore.play();
      this.queueStore.isPlayingIndex = 0;
      EventBus.emit('play_track', track);
      if (!track.isAudioOnly) {
        this.$router.push(`/playing/${track.id}`);
      }
    },
    removeSong(videoId) {
      fetch(`${this.prodUrl}/user/playlists/${this.$route.params.id}/songs/${videoId}`, {
        method: 'DELETE',
        credentials: 'include',
      }).then(async (res) => {
        res = await res.json();
        if (res.success) {
          this.tracks = this.tracks.filter((t) => t.id !== videoId);
          this.toast.success('Removed from playlist');
        } else {
          this.toast.error(res.message || 'Could not remove song');
        }
      }).catch(() => this.toast.error('An error occurred. Please try again.'));
    },
  },
};
</script>

<style scoped>
.cont {
  width: 100%;
  height: 100%;
  color: white;
  padding: 0 4%;
  box-sizing: border-box;
  overflow-y: auto;
  scrollbar-width: none;
}

.page-title {
  margin-left: 12px;
  font-weight: 600;
  font-size: 1.4rem;
}

.state-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 60%;
  color: #888;
  gap: 12px;
}

.list {
  margin-top: 2%;
  padding-bottom: 3%;
}

.listItem {
  display: flex;
  align-items: center;
  width: 100%;
  cursor: pointer;
  padding: 6px 0;
  border-radius: 8px;
  transition: background 0.2s;
}

.listItem:hover {
  background: #ffffff0d;
}

.thumb_cont {
  width: 60px;
  flex-shrink: 0;
}

.img-cont {
  height: 50px;
  width: 50px;
  overflow: hidden;
  border-radius: 6px;
}

.info {
  width: calc(100% - 60px);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-right: 12px;
}

.track-title {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.artist-name {
  color: grey;
  font-size: small;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: left;
}

@media (max-width: 675px) {
  .cont {
    padding: 0 2%;
  }
}
</style>
